import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

export interface ImageMultiSelectOption {
  value: string;
  label: string;
  imageUrl?: string | null;
  icon?: ReactNode;
  description?: string;
}

interface ImageMultiSelectProps {
  options: ImageMultiSelectOption[];
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyLabel?: string;
  searchable?: boolean;
  ariaLabel?: string;
  maxChips?: number;
}

export function ImageMultiSelect({
  options,
  values,
  onChange,
  placeholder,
  searchPlaceholder,
  emptyLabel,
  searchable = true,
  ariaLabel,
  maxChips = 2,
}: ImageMultiSelectProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [openUp, setOpenUp] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const selected = useMemo(
    () =>
      values
        .map((v) => options.find((o) => o.value === v))
        .filter((o): o is ImageMultiSelectOption => !!o),
    [values, options]
  );

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        o.value.toLowerCase().includes(q) ||
        (o.description?.toLowerCase().includes(q) ?? false)
    );
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  useEffect(() => {
    if (open && searchable) {
      const id = window.setTimeout(() => searchInputRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
  }, [open, searchable]);

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const estimateDropdownHeight = () => {
      const searchHeight = searchable ? 56 : 0;
      const listHeight = Math.min(filtered.length, 6) * 36 + 16;
      return Math.min(searchHeight + listHeight, 320);
    };
    const updateDirection = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const needed = estimateDropdownHeight() + 8;
      const below = window.innerHeight - rect.bottom;
      const above = rect.top;
      setOpenUp(below < needed && above > below);
    };
    updateDirection();
    window.addEventListener("resize", updateDirection);
    window.addEventListener("scroll", updateDirection, true);
    return () => {
      window.removeEventListener("resize", updateDirection);
      window.removeEventListener("scroll", updateDirection, true);
    };
  }, [open, searchable, filtered.length]);

  const toggle = (value: string) => {
    if (values.includes(value)) {
      onChange(values.filter((v) => v !== value));
    } else {
      onChange([...values, value]);
    }
  };

  const removeValue = (value: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(values.filter((v) => v !== value));
  };

  const clearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  };

  const renderOptionMedia = (opt: ImageMultiSelectOption) => {
    if (opt.imageUrl) {
      return (
        <img
          src={opt.imageUrl}
          alt=""
          className="h-6 w-6 shrink-0 rounded object-cover"
        />
      );
    }
    if (opt.icon !== undefined && opt.icon !== null) {
      return (
        <span className="flex h-6 w-6 shrink-0 items-center justify-center text-base leading-none">
          {opt.icon}
        </span>
      );
    }
    return <span className="h-6 w-6 shrink-0" aria-hidden />;
  };

  const visibleChips = selected.slice(0, maxChips);
  const overflowCount = selected.length - visibleChips.length;
  const resolvedPlaceholder = placeholder ?? t("common.select", "Pasirinkite...");

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="input flex min-h-[42px] w-full items-center gap-1.5 text-left"
      >
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
          {selected.length === 0 ? (
            <span className="truncate text-gray-400">{resolvedPlaceholder}</span>
          ) : (
            <>
              {visibleChips.map((opt) => (
                <span
                  key={opt.value}
                  className="inline-flex max-w-full items-center gap-1 rounded-md bg-primary-50 py-0.5 pl-1 pr-0.5 text-xs font-medium text-primary-800"
                >
                  {opt.imageUrl ? (
                    <img
                      src={opt.imageUrl}
                      alt=""
                      className="h-4 w-4 rounded object-cover"
                    />
                  ) : opt.icon !== undefined && opt.icon !== null ? (
                    <span className="flex h-4 w-4 items-center justify-center text-sm leading-none">
                      {opt.icon}
                    </span>
                  ) : null}
                  <span className="truncate">{opt.label}</span>
                  <span
                    role="button"
                    aria-label={t("common.remove", "Pašalinti")}
                    tabIndex={0}
                    onClick={(e) => removeValue(opt.value, e)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        e.stopPropagation();
                        onChange(values.filter((v) => v !== opt.value));
                      }
                    }}
                    className="flex h-4 w-4 items-center justify-center rounded hover:bg-primary-100"
                  >
                    <svg
                      className="h-3 w-3"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2.5}
                        d="M6 6l12 12M6 18L18 6"
                      />
                    </svg>
                  </span>
                </span>
              ))}
              {overflowCount > 0 && (
                <span className="text-xs text-gray-500">
                  +{overflowCount}
                </span>
              )}
            </>
          )}
        </div>

        {selected.length > 0 && (
          <span
            role="button"
            aria-label={t("common.clear", "Išvalyti")}
            tabIndex={0}
            onClick={clearAll}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                onChange([]);
              }
            }}
            className="ml-1 flex h-5 w-5 shrink-0 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <svg
              className="h-3.5 w-3.5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 6l12 12M6 18L18 6"
              />
            </svg>
          </span>
        )}

        <svg
          className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${
            open ? "rotate-180" : ""
          }`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {open && (
        <div
          className={`absolute left-0 right-0 z-30 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg ${
            openUp ? "bottom-full mb-1" : "top-full mt-1"
          }`}
          role="listbox"
        >
          {searchable && (
            <div className="border-b border-gray-100 p-2">
              <div className="relative">
                <svg
                  className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 103.5 10.5a7.5 7.5 0 0013.15 6.15z"
                  />
                </svg>
                <input
                  ref={searchInputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={
                    searchPlaceholder ?? t("common.search", "Ieškoti...")
                  }
                  className="w-full rounded-md border border-gray-200 bg-white py-1.5 pl-8 pr-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500/30"
                />
              </div>
            </div>
          )}

          <div className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-center text-sm text-gray-500">
                {emptyLabel ?? t("common.no_matches", "Rezultatų nerasta")}
              </div>
            ) : (
              filtered.map((opt) => {
                const isSelected = values.includes(opt.value);
                return (
                  <label
                    key={opt.value}
                    className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggle(opt.value)}
                      className="h-3.5 w-3.5 shrink-0 rounded accent-primary-800"
                    />
                    {renderOptionMedia(opt)}
                    <span className="min-w-0 flex-1 truncate">{opt.label}</span>
                    {opt.description && (
                      <span className="truncate text-xs text-gray-400">
                        {opt.description}
                      </span>
                    )}
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
